import type { CSSProperties } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { motion, useReducedMotion } from 'motion/react';
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Button,
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@monksflow/monks-ui';
import { ArrowLeftIcon, ArrowRightLeftIcon, ExternalLinkIcon } from 'lucide-react';
import { IconRail, RailRow, useUserOrganizations } from '@/widgets/app-shell';
import { ENV } from '@/lib/config/env';
import { ROUTES } from '@/shared/config/routes';
import { PanelReveal } from '@/shared/ui/PanelReveal';
import { motionTokens } from '@monksflow/monks-ui/preset';
import {
  isSectionActive,
  useOrganizationSettingsNav,
  type SettingsRailLink,
} from './useOrganizationSettingsNav';
import { settingsRailArea, useSettingsRailCollapsed } from './settings-rail.store';

/** The one expanded-sidebar width the app pins, the same figure `FeatureSidebar` uses. */
const SIDEBAR_WIDTH_PX = 280;

const TEST_ID = 'organization-settings-rail';

/**
 * One id for the whole rail, not one per `SidebarMenu`.
 *
 * The primitive scopes its own indicator per menu so two menus on a screen
 * cannot fight over it. Here the groups are one navigation with one selection,
 * so a per-group id would strand the indicator at a group boundary: moving
 * between two groups would fade rather than slide. Exactly one item is active
 * at a time, so a single id has a single claimant.
 */
const ACTIVE_INDICATOR_LAYOUT_ID = `${TEST_ID}-active-indicator`;

/**
 * The primitive's own `horizontal` active-indicator geometry, copied because
 * `asChild` is what suppresses it: the indicator cannot be injected around a
 * Slot without breaking Radix's single-child rule, so a consumer that needs a
 * real anchor has to draw it. Changing either of these means changing the
 * design system, not this line.
 */
const INDICATOR_CLASS = 'absolute inset-0 rounded-md bg-sidebar-accent';
const INDICATOR_CONTENT_CLASS = 'relative z-10 flex w-full items-center gap-1.5';

/**
 * The shared-element recipe the primitive uses for this indicator and the Tabs
 * one, so all three read as a single gesture.
 *
 * Read off Tessra's own motion tokens rather than copied: the package publishes
 * the same `--duration-*` / `--ease-*` scale as a JS mirror precisely because
 * motion cannot read the CSS properties, so a retune in the design system
 * reaches this indicator without anyone editing it.
 */
const INDICATOR_TRANSITION = {
  duration: motionTokens.duration.fast,
  ease: motionTokens.ease.inOut,
} as const;

const ORGANIZATION_BOX = 'flex w-full items-center gap-xs px-xs py-xs';

/**
 * Where to send someone who wants to work in a different organization.
 *
 * The platform screen owns the choice, so this hands it only where to come
 * back to: naming an organization here would be guessing at a parameter that
 * screen has never been shown to read. It is the same link the app sidebar's
 * workspace menu has always offered.
 *
 * The redirect is the origin rather than the open URL, because org-scoped
 * resources do not exist in the organization being moved to.
 */
const switchOrganizationUrl = () =>
  `${ENV.PLATFORM_BASE_URL}/switch-organization` +
  `?redirectUri=${encodeURIComponent(window.location.origin)}`;

/**
 * The settings rail: every place an organization's settings can be changed,
 * grouped by who owns the setting, with the way back to the app underneath.
 *
 * It replaces a tab bar that sat inside the content area, so it is the only
 * thing on screen naming the open section — hence the descendant-based active
 * state in `isSectionActive` rather than the tab bar's strict equality.
 *
 * Collapsed, it becomes the app's own 75px icon rail with these destinations,
 * since it is the only navigation on screen and every destination has to stay
 * reachable. The agent builder starts collapsed, since its canvas wants the
 * width; each area remembers what the person last chose. The toggle lives in
 * each page's header (`SettingsRailToggle`), not on the rail.
 *
 * It draws no surface of its own — hence `bg-transparent` over the primitive's
 * `bg-sidebar`: it sits directly on the page background, and the content column
 * beside it is the only card in the frame.
 */
export const OrganizationSettingsRail = () => {
  const { pathname } = useLocation();
  const collapsed = useSettingsRailCollapsed(settingsRailArea(pathname));

  // Each rail reveals in its own box, so one gives up its width while the other takes it and the
  // content beside them slides between the two widths. The closing one fades and turns inert, so
  // its links and landmark leave the tab order and the accessibility tree as soon as it starts.
  return (
    <div className="flex h-full">
      <PanelReveal isOpen={!collapsed} axis="width" fadeOnClose className="flex h-full">
        <SettingsRailContent collapsed={false} />
      </PanelReveal>
      <PanelReveal isOpen={collapsed} axis="width" fadeOnClose className="flex h-full">
        <SettingsRailContent collapsed />
      </PanelReveal>
    </div>
  );
};

function SettingsRailContent({ collapsed }: { collapsed: boolean }) {
  const { t } = useTranslation('admin');
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const groups = useOrganizationSettingsNav();
  const label = t('admin.organizationSettings.settingsSections');

  if (collapsed) {
    const open = (item: SettingsRailLink) => () => {
      if (item.isExternal) window.open(item.href, '_blank', 'noopener,noreferrer');
      else void navigate(item.href);
    };
    return (
      <IconRail
        label={label}
        iconOnly
        testId={TEST_ID}
        header={<RailOrganizationMark />}
        items={groups.flatMap((group) =>
          group.items.map((item) => ({
            id: item.id,
            label: item.label,
            ...(item.isExternal
              ? {
                  accessibleLabel: t('admin.organizationSettings.opensInNewTab', {
                    label: item.label,
                  }),
                }
              : {}),
            icon: null,
            IconComponent: item.IconComponent,
            active: !item.isExternal && isSectionActive(pathname, item.href),
            onClick: open(item),
          })),
        )}
        bottomItems={[
          {
            id: 'back',
            label: t('admin.organizationSettings.back'),
            icon: null,
            IconComponent: ArrowLeftIcon,
            onClick: () => void navigate(ROUTES.HOME),
          },
        ]}
      />
    );
  }

  // The wrapper is a full-viewport flex row by default; here it is one column
  // inside the settings frame, so it hands its height back to the layout.
  const providerStyle = {
    '--sidebar-width': `${String(SIDEBAR_WIDTH_PX)}px`,
    width: 'auto',
    minHeight: 0,
    height: '100%',
  } as CSSProperties;

  return (
    <SidebarProvider defaultOpen style={providerStyle}>
      <Sidebar
        collapsible="none"
        // The primitive is a plain div, which an `aria-label` alone does not
        // name — without this the rail is unreachable by landmark.
        role="navigation"
        aria-label={label}
        data-testid={TEST_ID}
        className="gap-sm bg-transparent px-xs py-sm border-r border-muted"
      >
        <SidebarHeader className="flex-row items-center gap-2xs p-0">
          <RailOrganization />
        </SidebarHeader>

        <SidebarContent className="gap-xl p-0">
          {groups.map((group) => (
            <SidebarGroup key={group.id} className="gap-2xs p-0">
              <SidebarGroupLabel asChild>
                <h3>{group.heading}</h3>
              </SidebarGroupLabel>
              <SidebarMenu>
                {group.items.map((item) => (
                  <RailLink
                    key={item.id}
                    item={item}
                    isActive={isSectionActive(pathname, item.href)}
                  />
                ))}
              </SidebarMenu>
            </SidebarGroup>
          ))}
        </SidebarContent>

        {/* Home rather than history: settings is reached from a menu that is on
            every screen, so there is no one page it was opened from, and stepping
            back through the sections just visited is not leaving settings.
            A button rather than another row, because leaving settings is not one
            more destination in the list above it. */}
        <SidebarFooter className="p-0">
          <Button asChild variant="outline" className="w-full">
            <Link to={ROUTES.HOME} data-testid={`${TEST_ID}-back`}>
              <ArrowLeftIcon className="size-4 shrink-0" aria-hidden />
              {t('admin.organizationSettings.back')}
            </Link>
          </Button>
        </SidebarFooter>
      </Sidebar>
    </SidebarProvider>
  );
}

/** The organization's mark alone, heading the collapsed rail. */
function RailOrganizationMark() {
  const { assignedOrganization } = useUserOrganizations();
  return (
    <RailRow>
      <span
        data-testid={`${TEST_ID}-organization`}
        title={assignedOrganization?.name}
        className="flex size-10 items-center justify-center"
      >
        {assignedOrganization && <OrganizationMark organization={assignedOrganization} />}
      </span>
    </RailRow>
  );
}

interface RailOrganizationLike {
  id: string;
  name: string;
  logoUrl?: string | null;
}

/**
 * Whose settings these are, at the top of the rail - and, for someone who
 * belongs to more than one organization, the way to the others.
 *
 * The box itself never becomes a control. The app cannot move a session
 * between organizations; only the platform screen can, so the affordance is a
 * link out to it rather than a list to choose from. A reader with a single
 * organization gets no link at all, which is what the sidebar's workspace menu
 * already does with this same flag - and what it does while the list is still
 * loading or after it failed, since neither can prove there is a choice.
 */
function RailOrganization() {
  const { t } = useTranslation('admin');
  const { assignedOrganization, hasMultipleOrganizations } = useUserOrganizations();

  if (!assignedOrganization) {
    return null;
  }

  return (
    <div data-testid={`${TEST_ID}-organization`} className={ORGANIZATION_BOX}>
      <OrganizationMark organization={assignedOrganization} />
      <span className="flex min-w-0 flex-1 flex-col text-left">
        <span className="type-body-md-strong truncate text-foreground">
          {assignedOrganization.name}
        </span>
        <span className="type-body-sm truncate text-muted-foreground">
          {t('admin.organizationSettings.organization')}
        </span>
      </span>

      {hasMultipleOrganizations && (
        // Its own provider, as every other redesigned surface with a tooltip
        // does: the delay this icon wants is not the app-wide default.
        <TooltipProvider delayDuration={300}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button asChild variant="ghost" size="icon-sm" className="shrink-0">
                <a
                  href={switchOrganizationUrl()}
                  aria-label={t('admin.organizationSettings.switchOrganization')}
                  data-testid={`${TEST_ID}-organization-switch`}
                >
                  <ArrowRightLeftIcon aria-hidden />
                </a>
              </Button>
            </TooltipTrigger>
            <TooltipContent>{t('admin.organizationSettings.switchOrganization')}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </div>
  );
}

/**
 * The organization's logo, or its initial where it has none.
 *
 * Decorative either way: the name sits beside it, so reading it out would only
 * repeat what the row already says. One size in both rails, so collapsing the
 * rail does not shrink the mark.
 */
function OrganizationMark({ organization }: { organization: RailOrganizationLike }) {
  return (
    <Avatar aria-hidden size="default" roundness="rounded" className="shrink-0">
      {organization.logoUrl && <AvatarImage src={organization.logoUrl} alt="" />}
      <AvatarFallback size="default" roundness="rounded">
        {organization.name.charAt(0).toUpperCase()}
      </AvatarFallback>
    </Avatar>
  );
}

interface RailLinkProps {
  item: SettingsRailLink;
  isActive: boolean;
}

/**
 * One destination, as the primitive's menu button wrapping a router link.
 *
 * `asChild` is what makes the row a real anchor rather than a button that
 * navigates, so the browser's own open-in-new-tab and copy-link still work.
 * That is also what costs the primitive's sliding indicator, which it injects
 * only when it owns the element - so the row draws it, to the primitive's own
 * `horizontal` spec.
 *
 * `isActive` is deliberately NOT handed to the button while the animated
 * indicator is on screen. It would set `data-active`, and the static
 * `data-[active=true]` fill paints the moment React commits - the destination
 * would light up before the slide reached it. The primitive suppresses its own
 * `data-active` for the same reason. `aria-current` is set on the anchor
 * instead, so the indicator being a paint detail never costs the semantics.
 */
function RailLink({ item, isActive }: RailLinkProps) {
  const { t } = useTranslation('admin');
  const testId = `${TEST_ID}-item-${item.id}`;
  // `useReducedMotion` returns null before it has read the query; only a true
  // reading suppresses the slide.
  const prefersReducedMotion = useReducedMotion() ?? false;
  const slides = isActive && !prefersReducedMotion;

  const content = (
    <>
      {slides && (
        <motion.div
          data-slot="sidebar-active-indicator"
          data-testid={`${TEST_ID}-active-indicator`}
          layoutId={ACTIVE_INDICATOR_LAYOUT_ID}
          className={INDICATOR_CLASS}
          transition={INDICATOR_TRANSITION}
        />
      )}
      <span className={INDICATOR_CONTENT_CLASS}>
        <span className="flex shrink-0 items-center" aria-hidden>
          <item.IconComponent className="size-4" />
        </span>
        <span className="min-w-0 flex-1 truncate">{item.label}</span>
        {item.isExternal && (
          <ExternalLinkIcon className="size-3.5 shrink-0 text-muted-foreground" aria-hidden />
        )}
      </span>
    </>
  );

  return (
    <SidebarMenuItem>
      <SidebarMenuButton asChild isActive={isActive && !slides}>
        {item.isExternal ? (
          // `noopener` is what keeps the opened tab from reaching back through
          // `window.opener`; `noreferrer` withholds the settings URL we came from.
          <a
            href={item.href}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={t('admin.organizationSettings.opensInNewTab', { label: item.label })}
            data-testid={testId}
          >
            {content}
          </a>
        ) : (
          <Link to={item.href} aria-current={isActive ? 'page' : undefined} data-testid={testId}>
            {content}
          </Link>
        )}
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
}
